import { FunctionComponent, ReactNode, RefObject } from "react";

import { PendingEmailBanner } from "@/pages/Private/pages/List/pages/Unassigned/components/PendingEmailBanner";
import { ListPerson } from "@/pages/Private/pages/List/schema/list";
import { classNames } from "@/utils/classNames";

interface ChatlogFrameProps {
	/** Lead-panel host (narrow): no right gutter beside the thread. */
	panelLayout: boolean;
	/** Blurs the thread while leads are selected in the list. */
	blurred: boolean;
	/** False while the inbox loads or is empty: only `children` render. */
	showColumn: boolean;
	/** The open lead; its pending email lookups show as a banner above the thread. */
	lead?: ListPerson;
	scrollRef: RefObject<HTMLDivElement>;
	/** The messages and draft cards; the only part of the column that scrolls. */
	thread: ReactNode;
	/** The composer and the strip above it, pinned below the thread. */
	footer: ReactNode;
	/** Modals, toasts and portals that belong to the chat but not to its column. */
	children?: ReactNode;
}

/**
 * The chat log's height chain (PREC-4465). The root takes `h-full` from its host, the column
 * fills it, and the thread is the column's one `flex-1 min-h-0` scroller, so the composer below
 * it always stays inside the host. The root used the `stretch` height keyword, which only
 * Chromium understands: Safari and Firefox drop the declaration, the column grows with its
 * messages, the thread never scrolls and the composer ends up below the screen. The
 * `css-stretch-sizing` gate rule keeps the keyword out.
 */
export const ChatlogFrame: FunctionComponent<ChatlogFrameProps> = ({
	panelLayout,
	blurred,
	showColumn,
	lead,
	scrollRef,
	thread,
	footer,
	children,
}) => (
	<div
		className={classNames("h-full w-full z-5", panelLayout ? "" : "pr-12", blurred ? "blur" : "")}
		data-testid="chatlog-frame"
	>
		{showColumn && (
			<div className="flex flex-col gap-3 w-full h-full">
				<PendingEmailBanner
					className="mx-4 mt-4"
					listPersonId={lead?.id}
					pendingEmails={lead?.prospect?.pendingEmails}
				/>
				<div
					className="w-full flex-1 min-h-0 flex items-start justify-start overflow-y-auto gap-3 flex-col p-3 pb-16 scrollbar-hide"
					data-testid="chatlog-scroller"
					ref={scrollRef}
				>
					{thread}
				</div>
				{footer}
			</div>
		)}
		{children}
	</div>
);
